import type { CoverTextAlign } from 'fym-shared/src/music24/coverDoc';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export interface CropRect {
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

/**
 * Where a picture of `imageWidth` × `imageHeight` goes in a square of `side`: it covers the square
 * (times `zoom`), and the focus point sits in the middle as far as the picture reaches. Pure.
 */
export function coverCropRect(
  imageWidth: number,
  imageHeight: number,
  side: number,
  focusX: number,
  focusY: number,
  zoom: number
): CropRect {
  if (imageWidth <= 0 || imageHeight <= 0) {
    return { dx: 0, dy: 0, dw: side, dh: side };
  }
  const scale = Math.max(side / imageWidth, side / imageHeight) * Math.max(1, zoom);
  const dw = imageWidth * scale;
  const dh = imageHeight * scale;
  return {
    dw,
    dh,
    dx: clamp(side / 2 - focusX * dw, side - dw, 0),
    dy: clamp(side / 2 - focusY * dh, side - dh, 0),
  };
}

/** The two end points of a CSS-like linear gradient at `angleDeg` in a square of `side`. Pure. */
export function gradientLine(angleDeg: number, side: number): { x0: number; y0: number; x1: number; y1: number } {
  // CSS: 0deg points up, 90deg points right.
  const radians = ((angleDeg - 90) * Math.PI) / 180;
  const half = (Math.abs(Math.cos(radians)) + Math.abs(Math.sin(radians))) * (side / 2);
  const centre = side / 2;
  return {
    x0: centre - Math.cos(radians) * half,
    y0: centre - Math.sin(radians) * half,
    x1: centre + Math.cos(radians) * half,
    y1: centre + Math.sin(radians) * half,
  };
}

/** The x of the start of a line of `lineWidth` in a box of `boxWidth` whose centre is at 0. Pure. */
export function lineStartX(align: CoverTextAlign, boxWidth: number, lineWidth: number): number {
  if (align === 'left') {
    return -boxWidth / 2;
  }
  return align === 'right' ? boxWidth / 2 - lineWidth : -lineWidth / 2;
}

/** The y of the middle of line `index` of `count` lines, in a block whose centre is at 0. Pure. */
export function lineMiddleY(index: number, count: number, lineHeightPx: number): number {
  return (index - (count - 1) / 2) * lineHeightPx;
}

/** A point turned by `degrees` about the origin. Pure. */
export function rotatePoint(x: number, y: number, degrees: number): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;
  return { x: x * Math.cos(radians) - y * Math.sin(radians), y: x * Math.sin(radians) + y * Math.cos(radians) };
}

/** An angle in -180..180. Pure. */
export function normaliseDegrees(degrees: number): number {
  const turned = ((((degrees + 180) % 360) + 360) % 360) - 180;
  return turned === -180 ? 180 : turned;
}
